import { Button, Chip, Stack, Typography } from "@mui/material";
import { DataTable } from "../data";
import { EmptyState, StatusChip } from "..";

function StatusTone(status) {
  switch (status) {
    case "active":
      return "success";
    case "pending":
      return "warning";
    case "inactive":
    case "archived":
      return "muted";
    default:
      return "neutral";
  }
}

function YesNoChip({ value, label }) {
  return value ? (
    <StatusChip tone="success" label={label ?? "Yes"} dot={false} />
  ) : (
    <StatusChip tone="muted" label={label ?? "No"} dot={false} />
  );
}

/**
 * Vendors data table — desktop view uses dense rows; phones get the
 * DataTable card-collapse automatically.
 */
export default function VendorTable({
  vendors,
  isMerchant,
  onEdit,
  onDelete,
  onViewAttachments,
}) {
  const columns = [
    {
      id: "name",
      label: "Name",
      primary: true,
      render: (vendor) => (
        <Stack spacing={0.5}>
          <Typography variant="body2" fontWeight={600}>
            {vendor.name}
          </Typography>
          {vendor.tag && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {vendor.tag
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean)
                .map((tag, idx) => (
                  <Chip
                    key={idx}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: 11, height: 20 }}
                  />
                ))}
            </Stack>
          )}
        </Stack>
      ),
    },
    {
      id: "notes",
      label: "Notes",
      width: 280,
      hideOnMobile: true,
      render: (vendor) => (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
          }}
        >
          {vendor.notes
            ? vendor.notes.length > 200
              ? `${vendor.notes.slice(0, 200)}…`
              : vendor.notes
            : "—"}
        </Typography>
      ),
    },
    {
      id: "category",
      label: "Category",
      render: (vendor) => vendor.category ?? "—",
    },
    {
      id: "status",
      label: "Status",
      render: (vendor) => (
        <StatusChip tone={StatusTone(vendor.status)} label={vendor.status} />
      ),
    },
    ...(isMerchant
      ? []
      : [
          {
            id: "serviceFee",
            label: "Service fee",
            align: "right",
            render: (vendor) =>
              vendor.serviceFeePercent != null
                ? `${Number(vendor.serviceFeePercent).toFixed(2)}%`
                : "—",
          },
          {
            id: "commission",
            label: "Commission",
            align: "right",
            render: (vendor) =>
              vendor.commissionPercent != null
                ? `${Number(vendor.commissionPercent).toFixed(2)}%`
                : "—",
          },
          {
            id: "featured",
            label: "Featured",
            render: (vendor) => <YesNoChip value={vendor.isFeatured} />,
          },
          {
            id: "partner",
            label: "Partner",
            render: (vendor) => (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                <YesNoChip value={vendor.isPartner} />
                {vendor.isNewPartner && (
                  <StatusChip tone="brand" label="New" dot={false} />
                )}
              </Stack>
            ),
          },
        ]),
    {
      id: "attachments",
      label: "Attachments",
      render: (vendor) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onViewAttachments(vendor)}
        >
          View
        </Button>
      ),
    },
    {
      id: "actions",
      label: "",
      align: "right",
      hideLabelOnMobile: true,
      render: (vendor) => (
        <Stack
          direction="row"
          spacing={1}
          justifyContent={{ xs: "flex-end", md: "flex-end" }}
        >
          <Button size="small" onClick={() => onEdit(vendor)}>
            Edit
          </Button>
          {!isMerchant && (
            <Button
              size="small"
              color="error"
              onClick={() => onDelete(vendor)}
            >
              Delete
            </Button>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={vendors}
      rowKey="id"
      emptyState={
        isMerchant ? (
          <EmptyState
            title="No vendors assigned yet"
            description="Your account doesn't have access to any vendors. Ask a teammate or admin to add you to a merchant team."
          />
        ) : (
          <EmptyState
            title="No vendors yet"
            description="Add your first vendor to start building your catalog."
          />
        )
      }
    />
  );
}
